import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popover,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { SvgIcon } from '@/components/icon';
import {
  AnalyticsApiKey,
  CreatedAnalyticsApiKey,
  createApiKey,
  deleteApiKey,
  getApiKeys,
  revokeApiKey,
} from '@/api/apiKeys';

const formatDate = (value: string | null) =>
  value ? format(new Date(value), 'd MMM, yyyy, h:mm a') : '—';

export default function AdminApiKeys() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState<CreatedAnalyticsApiKey | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<AnalyticsApiKey | null>(
    null,
  );
  const [form] = Form.useForm<{ name: string }>();

  const { data: apiKeys, isLoading } = useQuery<AnalyticsApiKey[]>({
    queryKey: ['analytics-api-keys'],
    queryFn: getApiKeys,
  });

  const { mutateAsync: createKeyMutation, isPending: isCreating } = useMutation({
    mutationFn: (name: string) => createApiKey(name),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['analytics-api-keys'] });
      setCreateOpen(false);
      form.resetFields();
      // Show the plaintext key exactly once — it can't be recovered later.
      setRevealKey(created);
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to create API key.');
    },
  });

  const { mutateAsync: revokeKeyMutation } = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-api-keys'] });
      message.success('API key revoked.');
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to revoke API key.');
    },
  });

  const { mutateAsync: deleteKeyMutation } = useMutation({
    mutationFn: (id: string) => deleteApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-api-keys'] });
      message.success('API key deleted.');
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to delete API key.');
    },
    onSettled: () => setDeleteTarget(null),
  });

  const copyKey = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success('Key copied to clipboard.');
    } catch {
      message.error('Could not copy — select and copy manually.');
    }
  };

  const rowActions = (record: AnalyticsApiKey) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {record.isActive && (
        <Button type="text" onClick={() => revokeKeyMutation(record.id)}>
          Revoke
        </Button>
      )}
      <Button type="text" danger onClick={() => setDeleteTarget(record)}>
        Delete
      </Button>
    </div>
  );

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Key',
      dataIndex: 'keyPrefix',
      key: 'keyPrefix',
      render: (prefix: string) => (
        <Typography.Text code>{`${prefix}…`}</Typography.Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Revoked'}
        </Tag>
      ),
    },
    {
      title: 'Last used',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (value: string | null) => formatDate(value),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string | null) => formatDate(value),
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, record: AnalyticsApiKey) => (
        <Popover
          placement="bottomRight"
          trigger="click"
          arrow={false}
          content={() => rowActions(record)}
        >
          <Button type="link">
            <SvgIcon type="threeDots" />
          </Button>
        </Popover>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: '0 0 24px' }}>
          Analytics API Keys
        </Typography.Title>
        <Button type="primary" onClick={() => setCreateOpen(true)}>
          Create API Key
        </Button>
      </Row>

      <Typography.Paragraph type="secondary" style={{ maxWidth: 760 }}>
        Keys authenticate the read-only analytics API (used by Lovable and other
        dashboard tools) via the{' '}
        <Typography.Text code>x-api-key</Typography.Text> header. The full key is
        shown only once at creation — store it safely. Revoke or delete a key
        here to cut off access immediately.
      </Typography.Paragraph>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={apiKeys || []}
        columns={columns}
        pagination={false}
      />

      {/* Create modal */}
      <Modal
        title={<div className="modal-title">Create API Key</div>}
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={460}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={({ name }) => createKeyMutation(name)}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Give the key a name' }]}
          >
            <Input placeholder="e.g. Lovable dashboards" maxLength={100} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isCreating}>
              Create
            </Button>
            <Button
              onClick={() => {
                setCreateOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Reveal-once modal */}
      <Modal
        title={<div className="modal-title">Copy your API key</div>}
        open={!!revealKey}
        onCancel={() => setRevealKey(null)}
        footer={
          <Button type="primary" onClick={() => setRevealKey(null)}>
            Done
          </Button>
        }
        width={520}
      >
        <Typography.Paragraph type="warning">
          This is the only time the full key will be shown. Copy it now and store
          it somewhere safe — you won’t be able to see it again.
        </Typography.Paragraph>
        <Space.Compact style={{ width: '100%' }}>
          <Input readOnly value={revealKey?.key} />
          <Button
            type="primary"
            onClick={() => revealKey && copyKey(revealKey.key)}
          >
            Copy
          </Button>
        </Space.Compact>
      </Modal>

      {/* Delete confirm modal */}
      <Modal
        title={
          <div className="modal-title">{`Delete ${deleteTarget?.name ?? ''}`}</div>
        }
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        footer={null}
        width={460}
      >
        <Space style={{ marginBottom: 16 }}>
          <Typography.Text>
            {`Delete "${deleteTarget?.name ?? ''}"? Any tool using this key will immediately lose access. This cannot be undone.`}
          </Typography.Text>
        </Space>
        <Space style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            type="primary"
            danger
            onClick={() => deleteTarget && deleteKeyMutation(deleteTarget.id)}
          >
            Delete
          </Button>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
        </Space>
      </Modal>
    </>
  );
}
